import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, TextField, Divider, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SettingsBackupRestoreOutlinedIcon from '@mui/icons-material/SettingsBackupRestoreOutlined';
import { TM_SETTINGS } from "../../../config/rtmSettingsSchema";
import SleekSingleSelect from "../SleekSingleSelect";
import UIRTMButton from "../UIRTMButton";
import "./RTMSettings.css";

import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import SecurityIcon from "@mui/icons-material/Security";

const TestManagementFeature = ({ projectId, siteId, config, onUpdate }) => {
  const [activeTM, setActiveTM] = useState("");
  const [tmConfig, setTmConfig] = useState({});
  const [initialState, setInitialState] = useState({
    activeTM: "",
    configs: {},
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {

    if (config) {
      const active = config.activeTM || "";
      const configs = config.tmConfigs || {};
      setActiveTM(active);
      setTmConfig(configs[active] || {});
      setInitialState({
        activeTM: active,
        configs: JSON.parse(JSON.stringify(configs)),
      });
    }
  }, [config]);

  const handleFieldChange = (field, value) => {
    setTmConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (newTypeKey) => {
    setActiveTM(newTypeKey);
    setTmConfig(initialState.configs[newTypeKey] || {});
  };

  const hasChanges = () => {
    if (activeTM !== initialState.activeTM) return true;
    const currentFields = tmConfig;
    const initialFields = initialState.configs[activeTM] || {};

    const fields = TM_SETTINGS[activeTM]?.fields || [];
    return fields.some(
      (f) => (currentFields[f.key] || "") !== (initialFields[f.key] || ""),
    );
  };

  const validateFields = () => {
    if (!activeTM || !TM_SETTINGS[activeTM]) return false;
    const fields = TM_SETTINGS[activeTM].fields;
    return fields.every((f) => (tmConfig[f.key] || "").trim().length > 0);
  };

  const resetData = () => {
    setActiveTM(initialState.activeTM);
    setTmConfig(initialState.configs[initialState.activeTM] || {});
    toast.info("Changes reverted");
  };

  const handleSave = async () => {
    if (!validateFields()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    setIsLoading(true);
    const userDetails = JSON.parse(sessionStorage.getItem("UserDetails"));
    const headerData = {
      Authorization: sessionStorage.getItem("authToken") || "",
      "ch-subscription-key":
        userDetails?.data?.User?.["ch-subscription-key"] || "",
    };

    const url = `${window.configs.BASE_API_URL}${window.configs.ADMIN_SERVICE_PROJECT_URL}/${projectId}/${siteId}/rtmSettings`;

   
    const updatedConfigs = { ...initialState.configs, [activeTM]: tmConfig };
    const payload = {
      rtm_settings: {
        activeALM: "jira",
        almProviders: {
          ["jira"]: {
            testManagement: {
              activeTM: activeTM,
              tmConfigs: updatedConfigs,
            },
          },
        },
      },
    };

    try {
      const result = await axios.patch(url, payload, { headers: headerData });
    
      if (result.data.status === "success") {
        toast.success("Test Management Configuration saved successfully");
        if (onUpdate) onUpdate();
      } else {
        toast.error(result.data.message || "Failed to save configuration");
      }
    } catch (error) {
      toast.error("Error saving configuration");
    } finally {
      setIsLoading(false);
    }
  };

  const tmOptions = Object.keys(TM_SETTINGS).map((key) => ({
    id: key, // "zephyrSquad"
    name: TM_SETTINGS[key].label, // "Zephyr Squad"
  }));

  return (
    <Box className="alm-feature-container">
      <p className="alert-info-label" style={{ textAlign: "center" }}>
        {TM_SETTINGS.description ||
          "Sync test cases and results with your preferred test management tool."}
      </p>
      <Divider sx={{ my: 2 }} />

      <Box className="alm-fields-stack">
        <Box className="alm-field-row">
          <label className="alm-dropdown-label">
            <BuildCircleIcon sx={{ color: "#3a85ef", fontSize: "1.1rem", mr: 1, verticalAlign: "middle" }} />
            Select TM Tool
          </label>
          <SleekSingleSelect
            placeholder="Select Tool"
            options={tmOptions}
            selectedValue={activeTM} // "zephyrSquad" / "xray"
            onChange={(id) => handleTypeChange(id)}
            isOpen={isDropdownOpen}
            setOpen={setDropdownOpen}
            width="300px"
          />
        </Box>

        {activeTM && TM_SETTINGS[activeTM] && (
          <Box className="alm-fields-stack">
            {TM_SETTINGS[activeTM].fields.map((field) => (
              <Box className="alm-field-row" key={field.key}>
                <label className="alm-field-label">
                  <SecurityIcon sx={{ color: "#3a85ef", fontSize: "1.1rem", mr: 1, verticalAlign: "middle" }} />
                  {field.label} <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </label>
                <TextField
                  fullWidth
                  size="small"
                  type={field.type || "text"}
                  variant="outlined"
                  placeholder={field.placeholder}
                  value={tmConfig[field.key] || ""}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#f9fafb",
                      "& fieldset": { borderColor: "#e5e7eb" },
                      "&:hover fieldset": { borderColor: "#3a85ef" },
                      "&.Mui-focused fieldset": { borderColor: "#3a85ef" },
                    }
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {activeTM && (
        <Box className="alm-buttons-container">
          <UIRTMButton
            variant="text"
            title="Cancel"
            disabled={!hasChanges() || isLoading}
            onClick={resetData}
             icon={<SettingsBackupRestoreOutlinedIcon sx={{fontSize:"0.9rem"}}/>}
          />
          <UIRTMButton
            variant="text"
            title={isLoading ? "Saving..." : "Save"}
            loading={isLoading}
            disabled={!validateFields() || !hasChanges() || isLoading}
            onClick={handleSave}
            icon={<SaveOutlinedIcon sx={{fontSize:"0.9rem"}}/>}
          />
        </Box>
      )}
    </Box>
  );
};

TestManagementFeature.propTypes = {
  projectId: PropTypes.string.isRequired,
  siteId: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  onUpdate: PropTypes.func,
};

export default TestManagementFeature;
